import type { QueryClient } from '@tanstack/react-query';
import { flushQueued } from '@/features/workspace/sendQueue';
import { isOnline, subscribeOnline } from './network';
import { OUTBOX_EVENT, pending } from './outbox';
import { dueOnReconnect, readLastSync, readSyncMode, SYNC_MODE_EVENT, syncPeriodMs, writeLastSync } from './syncMode';

/** Что освежается: переписки, документы, календарь, задачи и файлы. */
const ROOTS = ['ws', 'kanban'] as const;
const RETRY_MS = 15_000;

/** Перечитать офлайн-копию с сервера. */
export async function refreshCopy(client: QueryClient): Promise<void> {
  if (!isOnline()) return;
  await Promise.all(ROOTS.map((root) => client.invalidateQueries({ queryKey: [root] })));
  writeLastSync(Date.now());
}

/** Кнопка «Обновить сейчас»: очередь и копия. */
export async function syncNow(client: QueryClient): Promise<void> {
  await flushQueued(client);
  await refreshCopy(client);
}

/**
 * Расписание: при возвращении связи уходит очередь, затем — если пора по настройке — освежается копия;
 * пока очередь не пуста, а связь есть, отправка повторяется (сервер мог ответить 5xx).
 * Возвращает остановку; смену режима в настройках ловит сама.
 */
export function startSync(client: QueryClient): () => void {
  let mode = readSyncMode();
  let periodic: number | null = null;
  const schedule = () => {
    if (periodic !== null) window.clearInterval(periodic);
    periodic = null;
    const period = syncPeriodMs(mode);
    if (period !== null) periodic = window.setInterval(() => void refreshCopy(client), period);
    // «Сразу»: запросы перечитываются при возвращении связи; в остальных режимах — только по расписанию.
    client.setDefaultOptions({ ...client.getDefaultOptions(), queries: { ...client.getDefaultOptions().queries, refetchOnReconnect: mode === 'instant' } });
  };
  schedule();
  const onChange = () => {
    if (!isOnline()) return;
    void flushQueued(client).then(() => {
      if (dueOnReconnect(mode, readLastSync(), Date.now())) void refreshCopy(client);
    });
  };
  const unsubscribe = subscribeOnline(onChange);
  const retry = window.setInterval(() => {
    if (!isOnline()) return;
    void pending().then((items) => {
      if (items.length > 0) void flushQueued(client);
    });
  }, RETRY_MS);
  // Сообщение встало в очередь, когда связь есть (за более старыми): отправляем сразу, по порядку.
  const onQueued = () => {
    if (isOnline()) void flushQueued(client);
  };
  window.addEventListener(OUTBOX_EVENT, onQueued);
  // Страница открылась уже с очередью (закрыли с неотправленным) — отправить сразу.
  onChange();
  const onMode = () => {
    mode = readSyncMode();
    schedule();
  };
  window.addEventListener(SYNC_MODE_EVENT, onMode);
  return () => {
    unsubscribe();
    window.removeEventListener(SYNC_MODE_EVENT, onMode);
    window.removeEventListener(OUTBOX_EVENT, onQueued);
    window.clearInterval(retry);
    if (periodic !== null) window.clearInterval(periodic);
  };
}
